import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  type PeralatanToParameterUjiDraft,
  type PeralatanToParameterUjiRelationField,
} from '../../api/peralatanToParameterUji';
import { relationFieldToRows } from '../../api/jenisPengukuranPerlengkapan';
import { strapiListDynamicEntityPage, type DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { ChemInline } from '../../components/ChemInline';
import { MasterMultiCheckboxGroup } from '../../components/MasterMultiCheckboxGroup';
import { MetodaSamplingChemLabel, metodaSamplingPlainLabel } from '../../components/MetodaSamplingChemLabel';
import { useMasterEntityMiniListQuery } from '../../query/hooks';
import { useChecklistSectionSearch } from '../../sampling/checklistSectionSearch';

type ReferensiMode = 'exact' | 'prefix';

const REFERENSI_SECTIONS: Array<{
  field: PeralatanToParameterUjiRelationField;
  label: string;
  kategori: string;
  mode: ReferensiMode;
  search: 'tujuan' | 'larutan' | 'peralatanPengambilan' | 'peralatanUkur' | 'peralatanPendukung' | 'peralatanK3' | 'cara' | 'mutu' | 'transport' | 'administrasi' | 'pengawetan';
}> = [
  { field: 'tujuan_pengambilan', label: 'Tujuan pengambilan contoh', kategori: 'TUJUAN_SAMPLING', mode: 'exact', search: 'tujuan' },
  { field: 'larutan_penjerap', label: 'Larutan penyerap', kategori: 'LARUTAN_PENJERAP', mode: 'exact', search: 'larutan' },
  { field: 'peralatan_pengambilan_contoh', label: 'Peralatan pengambil contoh', kategori: 'PERALATAN_SAMPLING', mode: 'prefix', search: 'peralatanPengambilan' },
  { field: 'peralatan_pengukuran_lapangan', label: 'Peralatan pengukuran lapangan', kategori: 'PERALATAN_UKUR_LAP', mode: 'exact', search: 'peralatanUkur' },
  { field: 'peralatan_pendukung', label: 'Peralatan pendukung', kategori: 'PERALATAN_PENDUKUNG', mode: 'exact', search: 'peralatanPendukung' },
  { field: 'peralatan_k3', label: 'Peralatan K-3', kategori: 'PERALATAN_K3', mode: 'exact', search: 'peralatanK3' },
  { field: 'cara_pengambilan_contoh', label: 'Cara pengambilan contoh', kategori: 'CARA_SAMPLING_', mode: 'prefix', search: 'cara' },
  { field: 'pengendalian_mutu_lapangan', label: 'Pengendalian mutu lapangan', kategori: 'PENGENDALIAN_MUTU', mode: 'prefix', search: 'mutu' },
  { field: 'pengamanan_transportasi_contoh', label: 'Pengamanan dan transportasi contoh', kategori: 'PENGAMANAN_SAMPLING', mode: 'exact', search: 'transport' },
  { field: 'administrasi_contoh', label: 'Administrasi contoh', kategori: 'ADMINISTRASI_SAMPEL', mode: 'exact', search: 'administrasi' },
  { field: 'pengawetan', label: 'Pengawetan (air)', kategori: 'PENGAWETAN', mode: 'exact', search: 'pengawetan' },
];

function referensiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return (
    [String(row.kode_kategori ?? '').trim(), String(row.kode ?? '').trim(), String(row.deskripsi1 ?? '').trim()]
      .filter(Boolean)
      .join(' — ') || row.documentId
  );
}

function sniMethodOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [String(row.kode_sni ?? '').trim(), String(row.judul ?? '').trim()].filter(Boolean).join(' — ') || row.documentId;
}

function parameterOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [String(row.nama_parameter ?? '').trim(), String(row.simbol ?? '').trim()].filter(Boolean).join(' ') || row.documentId;
}

async function loadReferensiOptions(
  jwt: string,
  kodeKategori: string,
  mode: ReferensiMode,
): Promise<DynamicEntityRow[]> {
  const result = await strapiListDynamicEntityPage(jwt, 'referensis', {
    populate: '*',
    page: 1,
    pageSize: 1000,
    ...(mode === 'exact'
      ? { exactFilters: { kode_kategori: kodeKategori } }
      : { containsFilters: { kode_kategori: kodeKategori } }),
  });
  return result.rows.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
}

const PARAMETER_SEARCH_DEBOUNCE_MS = 500;

function appendParameterSearchRows(
  loaded: DynamicEntityRow[],
  remote: DynamicEntityRow[],
  selected: DynamicEntityRow | null,
): DynamicEntityRow[] {
  const seen = new Set<string>();
  const out: DynamicEntityRow[] = [];
  const push = (row: DynamicEntityRow | null | undefined) => {
    const documentId = String(row?.documentId ?? '').trim();
    if (!row || !documentId || seen.has(documentId)) return;
    seen.add(documentId);
    out.push(row);
  };
  if (selected && !loaded.some((row) => row.documentId === selected.documentId)) push(selected);
  for (const row of loaded) push(row);
  for (const row of remote) push(row);
  return out;
}

type Props = {
  jwt: string | null;
  draft: PeralatanToParameterUjiDraft;
  onChange: (next: PeralatanToParameterUjiDraft) => void;
};

export function PeralatanToParameterUjiEditor({ jwt, draft, onChange }: Props) {
  const sectionSearch = useChecklistSectionSearch(jwt);
  const parameterUjiQ = useMasterEntityMiniListQuery(jwt, 'parameter-ujis', Boolean(jwt), "{}", 20, true);
  const sniMethodQ = useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, 'sni-methods-master'],
    queryFn: async () => {
      const result = await strapiListDynamicEntityPage(jwt!, 'sni-methods', { populate: '*', page: 1, pageSize: 1000 });
      return result.rows.sort((a, b) => sniMethodOptionLabel(a).localeCompare(sniMethodOptionLabel(b), 'id'));
    },
    enabled: Boolean(jwt),
  });
  const parameterMetodaQ = useMasterEntityMiniListQuery(jwt, 'parameter-uji-metodas', Boolean(jwt));
  const referensiQueries = useQueries({
    queries: REFERENSI_SECTIONS.map((section) => ({
      queryKey: ['strapi', 'referensiOptions', jwt, section.field, section.kategori, section.mode],
      queryFn: () => loadReferensiOptions(jwt!, section.kategori, section.mode),
      enabled: Boolean(jwt),
    })),
  });

  const [parameterInput, setParameterInput] = useState('');
  const [parameterSearchInput, setParameterSearchInput] = useState('');
  const parameterQuery = useDebouncedValue(parameterSearchInput, PARAMETER_SEARCH_DEBOUNCE_MS).trim();
  const parameterOptions = useMemo(
    () =>
      [...(parameterUjiQ.data ?? [])].sort((a, b) =>
        parameterOptionLabel(a).localeCompare(parameterOptionLabel(b), 'id'),
      ),
    [parameterUjiQ.data],
  );
  const selectedParameterLabel = parameterOptionLabel(draft.parameter_uji);
  const parameterRemoteQ = useQuery({
    queryKey: ['strapi', 'parameter-ujis', 'keyword-search', jwt, parameterQuery],
    queryFn: async () => {
      const result = await strapiListDynamicEntityPage(jwt!, 'parameter-ujis', {
        populate: "{}",
        page: 1,
        pageSize: 50,
        containsFilters: { parameter: parameterQuery },
      });
      return result.rows;
    },
    enabled: Boolean(jwt) && parameterQuery.length > 0,
  });
  const parameterRemoteLoading = Boolean(parameterQuery) && parameterSearchInput.trim() === parameterQuery && parameterRemoteQ.isFetching;
  const loadedParameterIds = useMemo(
    () => new Set(parameterOptions.map((row) => row.documentId)),
    [parameterOptions],
  );
  const parameterChoices = useMemo(() => {
    const showRemote = parameterQuery.length > 0 && parameterSearchInput.trim() === parameterQuery;
    return appendParameterSearchRows(
      parameterOptions,
      showRemote ? (parameterRemoteQ.data ?? []) : [],
      draft.parameter_uji,
    );
  }, [draft.parameter_uji, parameterOptions, parameterQuery, parameterRemoteQ.data, parameterSearchInput]);

  useEffect(() => {
    setParameterInput(selectedParameterLabel);
  }, [draft.parameter_uji?.documentId, selectedParameterLabel]);

  const metodeOptions = useMemo(() => {
    const all = parameterMetodaQ.data ?? [];
    const parameterId = String(draft.parameter_uji?.documentId ?? '').trim();
    const matched = parameterId
      ? all.filter((row) => relationFieldToRows(row.parameter_uji).some((item) => item.documentId === parameterId))
      : [];
    const source = matched.length ? matched : all;
    return [...source].sort((a, b) => metodaSamplingPlainLabel(a).localeCompare(metodaSamplingPlainLabel(b), 'id'));
  }, [draft.parameter_uji?.documentId, parameterMetodaQ.data]);

  function setRelation(field: PeralatanToParameterUjiRelationField, rows: DynamicEntityRow[]) {
    onChange({ ...draft, [field]: rows });
  }

  return (
    <Stack spacing={2} data-testid="peralatan-to-parameter-uji-editor">
      <Autocomplete
        options={parameterChoices}
        value={draft.parameter_uji}
        loading={parameterUjiQ.isPending || parameterRemoteLoading}
        loadingText="Mencari…"
        inputValue={parameterInput}
        onInputChange={(_, value, reason) => {
          setParameterInput(value);
          if (reason === 'input' || reason === 'clear') setParameterSearchInput(value);
        }}
        filterOptions={(options, state) => {
          const query = state.inputValue.trim().toLowerCase();
          const base = options.filter(
            (row) => loadedParameterIds.has(row.documentId) || row.documentId === draft.parameter_uji?.documentId,
          );
          const remote = options.filter(
            (row) => !loadedParameterIds.has(row.documentId) && row.documentId !== draft.parameter_uji?.documentId,
          );
          const filteredBase = query
            ? base.filter((row) => parameterOptionLabel(row).toLowerCase().includes(query))
            : base;
          return [...filteredBase, ...remote];
        }}
        noOptionsText={parameterRemoteLoading ? 'Mencari…' : 'Tidak ada parameter uji yang cocok.'}
        onChange={(_, row) => {
          const parameterId = String(row?.documentId ?? '').trim();
          onChange({
            ...draft,
            parameter_uji: row,
            metode_sampling: parameterId
              ? draft.metode_sampling.filter((item) => {
                  const linked = relationFieldToRows(item.parameter_uji).map((rel) => rel.documentId);
                  return linked.length === 0 || linked.includes(parameterId);
                })
              : [],
          });
        }}
        getOptionLabel={parameterOptionLabel}
        isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
        renderOption={(props, row) => {
          return (
          <li {...props} key={row.documentId}>
            <ChemInline text={String(row.nama_parameter ?? '')} />
          </li>
        )}}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Parameter uji"
            size="small"
            required
            inputProps={{ ...params.inputProps, 'data-testid': 'peralatan-to-parameter-uji-parameter' }}
          />
        )}
      />
      <MasterMultiCheckboxGroup
        label="Metode sampling"
        options={sniMethodQ.data ?? []}
        value={draft.sni_methods}
        onChange={(rows) => setRelation('sni_methods', rows)}
        getOptionLabel={sniMethodOptionLabel}
        loading={sniMethodQ.isPending}
        onSearchMiss={sectionSearch?.sni}
        data-testid="peralatan-to-parameter-uji-sni"
      />
      <MasterMultiCheckboxGroup
        label="Parameter"
        options={metodeOptions}
        value={draft.metode_sampling}
        onChange={(rows) => setRelation('metode_sampling', rows)}
        getOptionLabel={metodaSamplingPlainLabel}
        renderOptionLabel={(row) => <MetodaSamplingChemLabel row={row} />}
        loading={parameterMetodaQ.isPending}
        onSearchMiss={sectionSearch?.parameter}
        data-testid="peralatan-to-parameter-uji-metode"
      />
      {REFERENSI_SECTIONS.map((section, index) => (
        <MasterMultiCheckboxGroup
          key={section.field}
          label={section.label}
          options={referensiQueries[index]?.data ?? []}
          value={draft[section.field]}
          onChange={(rows) => setRelation(section.field, rows)}
          getOptionLabel={referensiOptionLabel}
          loading={referensiQueries[index]?.isPending}
          onSearchMiss={sectionSearch?.[section.search]}
          data-testid={`peralatan-to-parameter-uji-${section.field}`}
        />
      ))}
      <TextField
        label="Jumlah wadah contoh uji air"
        size="small"
        value={draft.jumlah_wadah_contoh_uji_air}
        onChange={(event) => onChange({ ...draft, jumlah_wadah_contoh_uji_air: event.target.value })}
        fullWidth
      />
      <TextField
        label="Jumlah wadah contoh uji padatan"
        size="small"
        value={draft.jumlah_wadah_contoh_uji_padatan}
        onChange={(event) => onChange({ ...draft, jumlah_wadah_contoh_uji_padatan: event.target.value })}
        fullWidth
      />
    </Stack>
  );
}
